import React, { useState } from "react";
import { validateAll } from "indicative/validator";
import FloatingLabelInput from "../../../components/elements/Inputs/FloatingLabelInput";
import DrawerPanel from "../../../components/elements/DrawerPanel/DrawerPanel";
import { DbAllTerminals } from "../../../types/api.types";
import { DrawerButton } from "../../../components/elements/Button";
import { handleValidationError } from "../../../utils/formHandlers";
import { replaceTerminalApi } from "../../../redux/slice/posterminals/posterminals.api";
import Loading from "../../../components/elements/Loading/Loading";
import { ErrorToast, SuccessToast } from "../../../components/blocks/Alert";

const getDesc = () => "Replace Damaged Terminal for communities/entities";
type Props = {
    handleClose(): void;
    row: DbAllTerminals;
};

const ReplaceTerminals = (props: Props) => {
    const { handleClose, row } = props;

    const [formData, setFormData] = useState({
        newSerialNumber: "",
        serviceCharge: "",
        fullPayment: false,
        isFree: false,
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);

    const handleTerminalReplacement = async () => {
        setLoading(true);
        try {
            const payload: Record<string, any> = {
                oldTerminalId: row.terminalId,
                newSerialNumber: formData.newSerialNumber,
            };

            if (formData.fullPayment) payload.fullPayment = true;
            if (formData.isFree) payload.isFree = true;
            if (formData.serviceCharge !== "") {
                payload.serviceCharge = Number(formData.serviceCharge);
            }

            const res = await replaceTerminalApi(payload as any);
            SuccessToast(res?.message ?? "Terminal Replacement Successful");
            handleClose();
        } catch (error) {
            ErrorToast(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (e: React.SyntheticEvent) => {
        e.preventDefault();

        const rules = {
            newSerialNumber: "required",
        };

        const message = {
            "newSerialNumber.required": "Serial Number cannot be blank",
        };

        validateAll(formData, rules, message)
            .then(() => {
                setErrors({});
                handleTerminalReplacement();
            })
            .catch(err => {
                const formattedErr = handleValidationError(err);
                setErrors(formattedErr);
            });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value, name, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    return (
        <DrawerPanel
            title="Replace Terminal"
            description={getDesc()}
            open
            handleClose={handleClose}
        >
            <form onSubmit={handleSubmit}>
                <FloatingLabelInput
                    name="newSerialNumber"
                    label="New Serial Number"
                    value={formData.newSerialNumber}
                    onChange={handleChange}
                    error={errors.newSerialNumber}
                />
                <FloatingLabelInput
                    name="serviceCharge"
                    label="Service Charge (optional)"
                    value={formData.serviceCharge}
                    onChange={handleChange}
                    type="number"
                />

                <div className="flex flex-col gap-3 mt-4 mb-6">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            name="fullPayment"
                            checked={formData.fullPayment}
                            onChange={handleChange}
                            className="w-4 h-4 accent-[#995C12]"
                        />
                        <span className="text-sm font-avenirBook text-[#344054]">
                            Full Payment
                        </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            name="isFree"
                            checked={formData.isFree}
                            onChange={handleChange}
                            className="w-4 h-4 accent-[#995C12]"
                        />
                        <span className="text-sm font-avenirBook text-[#344054]">
                            Free Replacement
                        </span>
                    </label>
                </div>

                <DrawerButton type="submit">
                    {loading ? <Loading size="xs" /> : "Replace Terminal"}
                </DrawerButton>
            </form>
        </DrawerPanel>
    );
};

export default ReplaceTerminals;
